import type { ReactElement } from 'react';

import type { StreamState, TraceEntry } from '@harness/stream-core';

type Gap = Extract<TraceEntry, { kind: 'gap' }>;

/**
 * The honesty component. When ids were skipped, the reader is told that
 * output was lost and how much, in event terms, with the permanent case
 * named. A gap is never presented as continuous text; this notice is what
 * that rule looks like on screen.
 */
export function GapNotice({ state }: { state: StreamState }): ReactElement | null {
  const gaps = state.timeline.filter((entry): entry is Gap => entry.kind === 'gap');
  if (gaps.length === 0) return null;

  const lost = gaps.reduce((sum, gap) => sum + Math.max(0, gap.toId - gap.fromId - 1), 0);
  if (lost === 0) return null;

  return (
    <div className="notice notice--hold" role="status" aria-live="polite">
      <span className="notice__title">Output lost</span>
      <p>
        <span className="mono">{lost}</span>
        {lost === 1 ? ' event was' : ' events were'} dropped and could not be recovered. The text
        before and after each gap is intact; the gaps are marked here rather than smoothed over.
      </p>
      <ul className="gap-notice__list">
        {gaps.map((gap) => (
          <li key={`${String(gap.fromId)}-${String(gap.toId)}`}>
            <span className="mono">
              {gap.toId - gap.fromId === 2
                ? `event ${String(gap.fromId + 1)}`
                : `events ${String(gap.fromId + 1)} to ${String(gap.toId - 1)}`}
            </span>
            {gap.permanent && <span className="muted"> (given up after three attempts)</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
